import formatDate from "@lib/common/utils/formatDate";
import type { TextProps } from "@mantine/core";
import { Text, Tooltip } from "@mantine/core";
import { isDate } from "lodash-es";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

function timeAgo(date: Date | number, lang = navigator.language): string {
  // Allow dates or times to be passed
  const timeMs = typeof date === "number" ? date : date.getTime();

  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000);

  // Array representing one minute, hour, day, week, month, etc in seconds
  const cutoffs = [60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity];

  // Array equivalent to the above but in the string representation of the units
  const units: Intl.RelativeTimeFormatUnit[] = ["second", "minute", "hour", "day", "week", "month", "year"];

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(cutoff => cutoff > Math.abs(deltaSeconds));

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;

  // Intl.RelativeTimeFormat do its magic
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex]);
}

export default function TimeAgo({
  date,
  interval = 1000 * 30,
  prefix,
  suffix,
  ...props
}: { date: Date; interval?: number; prefix?: ReactNode; suffix?: ReactNode } & TextProps) {
  if (!isDate(date)) {
    throw new Error("Invalid date passed to TimeAgo component");
  }

  const [result, setResult] = useState<string>(() => timeAgo(new Date(date)));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResult(timeAgo(new Date(date)));
    const timer = setInterval(() => setResult(timeAgo(new Date(date))), interval);
    return () => clearInterval(timer);
  }, [date, interval]);

  return (
    <Tooltip label={formatDate(date)}>
      <Text component="span" {...props}>
        {prefix}
        {result}
        {suffix}
      </Text>
    </Tooltip>
  );
}
